import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { InternalUserService } from '../../services/internal-user.service';
import { ProfileService } from '../../services/profile.service';
import { ToastrService } from 'ngx-toastr';
import { Router, ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { InternalUserV2Input, InternalUserV2UpdateInput, InternalUserV2Output } from '../../services/models/internal-user.model';
import { ProfileOutput } from '../../services/models/profile.model';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-internal-user-create',
  standalone: true,
  imports: [FormsModule, CommonModule, NgSelectModule, TranslocoModule],
  templateUrl: './internal-user-create.component.html',
  styleUrl: './internal-user-create.component.css'
})
export class InternalUserCreateComponent implements OnInit {
  email: string = '';
  password: string = '';
  confirmPassword: string = '';
  selectedProfileIds: string[] = [];
  
  profiles: ProfileOutput[] = [];
  formSubmitted: boolean = false;
  userId: string | null = null;
  isEditMode: boolean = false;

  constructor(
    private internalUserService: InternalUserService,
    private profileService: ProfileService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private route: ActivatedRoute,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.loadProfiles();
    
    this.route.paramMap.subscribe(params => {
      this.userId = params.get('userId');
      this.isEditMode = !!this.userId;

      if (this.isEditMode && this.userId) {
        this.loadUser(this.userId);
      }
    });
  }

  loadProfiles(): void {
    this.profileService.getAll()
      .subscribe({
        next: (profiles) => {
          this.profiles = profiles;
        },
        error: (error) => {
          console.error('Erro ao carregar perfis:', error);
          this.toastr.error(this.transloco.translate('users.toast.loadProfilesListError'));
        }
      });
  }

  loadUser(userId: string): void {
    this.spinner.show();
    this.internalUserService.getById(userId).subscribe({
      next: (user) => {
        this.email = user.email;
        this.selectedProfileIds = user.profileIds ? [...user.profileIds] : [];
        console.log('Perfis carregados do usuário:', this.selectedProfileIds);
        this.spinner.hide();
      },
      error: (error) => {
        console.error('Erro ao carregar usuário:', error);
        this.toastr.error(this.transloco.translate('users.toast.loadUserError'));
        this.spinner.hide();
        this.router.navigate(['/users']);
      }
    });
  }

  onProfileChange(selectedIds: any): void {
    // Garantir que sempre seja um array de strings (IDs apenas)
    if (Array.isArray(selectedIds)) {
      // Se for array de objetos, extrair apenas os IDs
      this.selectedProfileIds = selectedIds.map(item => {
        if (typeof item === 'string') {
          return item; // Já é um ID
        } else if (item && typeof item === 'object' && item.id) {
          return item.id; // Extrair ID do objeto
        }
        return String(item); // Converter para string como fallback
      }).filter(id => id); // Remover valores vazios
    } else if (selectedIds) {
      // Se for um objeto único, extrair o ID
      if (typeof selectedIds === 'string') {
        this.selectedProfileIds = [selectedIds];
      } else if (selectedIds && typeof selectedIds === 'object' && selectedIds.id) {
        this.selectedProfileIds = [selectedIds.id];
      } else {
        this.selectedProfileIds = [String(selectedIds)];
      }
    } else {
      // Se for null ou undefined, usar array vazio
      this.selectedProfileIds = [];
    }
    console.log('Perfis selecionados alterados (apenas IDs):', this.selectedProfileIds);
  }

  save() {
    this.formSubmitted = true;

    // Validações para criação
    if (!this.isEditMode) {
      if (!this.email || !this.password || !this.confirmPassword) {
        return;
      }
      if (!this.isValidEmail(this.email)) {
        return;
      }
      if (this.password.length < 6) {
        return;
      }
      if (this.password !== this.confirmPassword) {
        return;
      }
    } else {
      // Validações para edição
      if (this.email && !this.isValidEmail(this.email)) {
        return;
      }
      if (this.password) {
        if (this.password.length < 6) {
          return;
        }
        if (!this.confirmPassword || this.password !== this.confirmPassword) {
          return;
        }
      }
    }

    this.spinner.show();

    if (this.isEditMode && this.userId) {
      this.updateUser();
    } else {
      this.createUser();
    }
  }

  createUser(): void {
    // Garantir que seja um array de strings (IDs apenas)
    const profileIdsArray = this.extractProfileIds(this.selectedProfileIds);

    const userInput: InternalUserV2Input = {
      email: this.email.toLowerCase().trim(),
      password: this.password,
      confirmPassword: this.confirmPassword,
      profileIds: profileIdsArray.length > 0 ? profileIdsArray : undefined
    };

    console.log('Enviando profileIds na criação (apenas IDs):', userInput.profileIds);

    this.internalUserService.create(userInput)
      .subscribe({
        next: () => {
          this.toastr.success(this.transloco.translate('users.toast.userCreated'));
          this.spinner.hide();
          this.router.navigate(['/users']);
        },
        error: (error) => {
          console.error('Erro ao criar usuário:', error);
          console.error('Payload enviado:', userInput);
          this.handleError(error, 'criar');
          this.spinner.hide();
        }
      });
  }

  updateUser(): void {
    const userUpdate: InternalUserV2UpdateInput = {};

    if (this.email) {
      userUpdate.email = this.email.toLowerCase().trim();
    }
    if (this.password) {
      userUpdate.password = this.password;
      userUpdate.confirmPassword = this.confirmPassword;
    }
    // Sempre enviar profileIds como array de strings (IDs apenas)
    // Isso garante que perfis sejam removidos se necessário
    userUpdate.profileIds = this.extractProfileIds(this.selectedProfileIds);

    console.log('Enviando profileIds (apenas IDs):', userUpdate.profileIds);

    this.internalUserService.update(this.userId!, userUpdate)
      .subscribe({
        next: () => {
          this.toastr.success(this.transloco.translate('users.toast.userUpdated'));
          this.spinner.hide();
          this.router.navigate(['/users']);
        },
        error: (error) => {
          console.error('Erro ao atualizar usuário:', error);
          console.error('Payload enviado:', userUpdate);
          this.handleError(error, 'atualizar');
          this.spinner.hide();
        }
      });
  }

  handleError(error: any, action: string): void {
    if (error.status === 400) {
      const errorMessage = error.error?.message || this.transloco.translate('users.toast.userDataError');
      this.toastr.error(errorMessage);
    } else if (error.status === 404) {
      this.toastr.error(this.transloco.translate('users.toast.userNotFound'));
    } else if (error.status === 409) {
      this.toastr.error(this.transloco.translate('users.toast.emailInUse'));
    } else {
      this.toastr.error(this.transloco.translate('users.toast.userGenericError'));
    }
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  cancelar() {
    this.router.navigate(['/users']);
  }

  /**
   * Extrai apenas os IDs de um array que pode conter objetos ou strings
   */
  private extractProfileIds(profileIds: any): string[] {
    if (!profileIds) {
      return [];
    }

    if (Array.isArray(profileIds)) {
      return profileIds.map(item => {
        if (typeof item === 'string') {
          return item; // Já é um ID
        } else if (item && typeof item === 'object' && item.id) {
          return item.id; // Extrair ID do objeto
        }
        return String(item); // Converter para string como fallback
      }).filter(id => id && typeof id === 'string'); // Garantir que sejam strings válidas
    } else if (typeof profileIds === 'string') {
      return [profileIds];
    } else if (profileIds && typeof profileIds === 'object' && profileIds.id) {
      return [profileIds.id];
    }

    return [];
  }
}

